import {useEffect, useState, useCallback} from 'react';
import {api} from '../lib/auth.js';
import {kr} from '../lib/format.js';
import StatementImport from './StatementImport.jsx';

const BLANK = () => ({account: '', debit: '', credit: '', text: ''});

/**
 * Skuggbokföring — provisional entries for what the accountant has not booked yet.
 *
 * The ledger closes weeks behind the business, so a figure that waits for it arrives when
 * it is a record rather than a decision. This tab lets a human fill the gap and see the
 * result now, on three conditions that the UI enforces rather than merely mentions:
 *
 * - Nothing here ever reaches Fortnox. There is no push path in the API at all.
 * - Every figure computed with these entries applied is LABELLED as such, and the toggle
 *   is off by default, so a provisional number can never be mistaken for a booked one.
 * - It is all removable in one act, because provisional data that is hard to withdraw
 *   stops being provisional.
 *
 * The entry form takes debit and credit rather than a signed amount. That is how the
 * source documents read, and asking someone transcribing a payroll slip to negate the
 * credit side in their head is how sign errors get in.
 */
export default function Shadow({active, onToggle}) {
	const [data, setData] = useState(null);
	const [superseded, setSuperseded] = useState([]);
	const [error, setError] = useState(null);
	const [busy, setBusy] = useState(false);
	const [form, setForm] = useState({
		date: '', description: '', note: '', seals_month: false, rows: [BLANK(), BLANK()],
	});

	const load = useCallback(async () => {
		setBusy(true);
		try {
			const [a, b] = await Promise.all([api('/api/shadow'), api('/api/shadow/superseded')]);
			if (!a.ok) throw new Error(`API svarade ${a.status}`);
			setData(await a.json());
			setSuperseded(b.ok ? (await b.json()).superseded : []);
			setError(null);
		} catch (e) { setError(e.message); } finally { setBusy(false); }
	}, []);

	useEffect(() => { load(); }, [load]);

	const setRow = (i, k, v) => setForm(f => ({
		...f, rows: f.rows.map((r, j) => (j === i ? {...r, [k]: v} : r)),
	}));

	// The running difference is shown while typing, not only on submit. A voucher that does
	// not balance is refused by the API either way, but finding that out after filling in
	// six rows is a worse experience than watching the figure close as you go.
	const delta = form.rows.reduce((a, r) => a + (Number(r.debit) || 0) - (Number(r.credit) || 0), 0);

	const submit = async () => {
		setBusy(true);
		try {
			const res = await api('/api/shadow', {
				method: 'POST',
				body: JSON.stringify({
					date: form.date, description: form.description, note: form.note || undefined,
					seals_month: form.seals_month,
					rows: form.rows.filter(r => r.account !== '')
						.map(r => ({account: Number(r.account), debit: Number(r.debit) || 0,
							credit: Number(r.credit) || 0, text: r.text || undefined})),
				}),
			});
			const json = await res.json();
			if (!res.ok) throw new Error(json.error ?? `API svarade ${res.status}`);
			setForm({date: '', description: '', note: '', seals_month: false, rows: [BLANK(), BLANK()]});
			await load();
		} catch (e) { setError(e.message); } finally { setBusy(false); }
	};

	const remove = async id => {
		if (!window.confirm(`Ta bort skuggverifikat ${id}?`)) return;
		await api(`/api/shadow/${id}`, {method: 'DELETE'});
		await load();
	};

	// Two-step, and the second step names the count. This discards work that exists nowhere
	// else — shadow bookings are by definition not in Fortnox — so a single misplaced click
	// must not be enough to lose it.
	const removeAll = async () => {
		const n = data?.vouchers.length ?? 0;
		if (!n) return;
		if (!window.confirm(`Ta bort ALLA ${n} skuggverifikat? Detta går inte att ångra.`)) return;
		if (!window.confirm('Bekräfta en gång till: all skuggbokföring raderas permanent.')) return;
		setBusy(true);
		await api('/api/shadow?confirm=1', {method: 'DELETE'});
		await load();
		setBusy(false);
	};

	if (error && !data) return <div className="state">Kunde inte hämta skuggbokföringen: {error}</div>;
	if (!data) return <div className="state">Hämtar…</div>;

	const {vouchers, summary} = data;

	return (
		<>
			<p className="sub">
				Preliminära verifikat som ligger enbart i Jabr. De skickas aldrig till Fortnox och
				påverkar inga siffror förrän du slår på dem.
			</p>

			<div className="toolbar">
				<label className="cashset">
					<input type="checkbox" checked={active} onChange={e => onToggle(e.target.checked)} />
					<span className="cashset__label">Räkna med skuggbokföring i alla vyer</span>
				</label>
				<span className="stamp">
					{summary.count} skuggverifikat
					{summary.months.length ? ` · ${summary.months.map(m => m.month).join(', ')}` : ''}
				</span>
				<span className="spacer" />
				<button className="btn" onClick={load} disabled={busy}>Uppdatera</button>
				<button className="btn" onClick={removeAll} disabled={busy || !vouchers.length}>
					Radera all skuggbokföring
				</button>
			</div>

			{error && <div className="panel panel--note"><p>{error}</p></div>}

			{active && (
				<div className="panel panel--note">
					<div className="panel__title">Siffrorna i övriga vyer innehåller skuggbokföring</div>
					<p>
						Dashboard, Runway och rapporterna räknar just nu med {summary.count} preliminära
						verifikat. Det är inte bokförda utfall — de ligger bara i Jabr och försvinner
						i samma sekund du stänger av rutan ovan eller raderar dem.
					</p>
				</div>
			)}

			{superseded.length > 0 && (
				<div className="panel panel--note">
					<div className="panel__title">
						Revisorn verkar ha hunnit ikapp — {superseded.length} skuggverifikat kan vara dubbelbokade
					</div>
					<p>
						Fortnox har bokfört rörelser på konton som dessa skuggverifikat täcker, efter att de
						lades in. Ligger båda kvar räknas beloppet två gånger. Kontrollera och ta bort det
						som blivit riktigt bokfört.
					</p>
					<ul className="assump">
						{superseded.map(s => (
							<li key={s._id}>
								<b>{s._id}</b> {s.date} {s.description} —
								{s.unknown
									? ' saknar baslinje och kan inte bedömas automatiskt.'
									: ` konto ${s.moved.map(m => `${m.account} (${kr(m.moved)} kr nytt)`).join(', ')}.`}
							</li>
						))}
					</ul>
				</div>
			)}

			<StatementImport shadow={active} onCreated={load} />

			<div className="charts--pair">
				<div className="table-wrap">
					<div className="chart__head" style={{padding: '14px 16px 0'}}>
						<span className="chart__title">Nytt skuggverifikat</span>
						<span className="chart__hint">debet och kredit i kronor, som på underlaget</span>
					</div>
					<div style={{padding: '12px 16px 18px'}}>
						<div className="fieldrow">
							<label className="field">
								<span>Datum</span>
								<input className="search" placeholder="2026-08-31" value={form.date}
									onChange={e => setForm(f => ({...f, date: e.target.value}))} />
							</label>
							<label className="field field--grow">
								<span>Beskrivning</span>
								<input className="search" placeholder="Löner augusti 2026 (preliminär)"
									value={form.description}
									onChange={e => setForm(f => ({...f, description: e.target.value}))} />
							</label>
						</div>

						<table style={{minWidth: 0, marginTop: 10}}>
							<thead><tr>
								<th>Konto</th><th>Debet</th><th>Kredit</th><th>Text</th>
							</tr></thead>
							<tbody>
								{form.rows.map((r, i) => (
									<tr key={i}>
										<td><input className="search cell" inputMode="numeric" value={r.account}
											placeholder="7210" onChange={e => setRow(i, 'account', e.target.value)} /></td>
										<td><input className="search cell" inputMode="decimal" value={r.debit}
											onChange={e => setRow(i, 'debit', e.target.value)} /></td>
										<td><input className="search cell" inputMode="decimal" value={r.credit}
											onChange={e => setRow(i, 'credit', e.target.value)} /></td>
										<td><input className="search cell cell--wide" value={r.text}
											onChange={e => setRow(i, 'text', e.target.value)} /></td>
									</tr>
								))}
							</tbody>
							<tfoot><tr>
								<td>Differens</td>
								<td colSpan={3} className={delta === 0 ? undefined : 'num--strong'}>
									{delta === 0 ? 'balanserar' : `${kr(delta)} kr — måste bli 0`}
								</td>
							</tr></tfoot>
						</table>

						<div className="fieldrow" style={{marginTop: 10}}>
							<button className="btn btn--sm"
								onClick={() => setForm(f => ({...f, rows: [...f.rows, BLANK()]}))}>
								Lägg till rad
							</button>
							<label className="cashset">
								<input type="checkbox" checked={form.seals_month}
									onChange={e => setForm(f => ({...f, seals_month: e.target.checked}))} />
								<span className="cashset__label">Månaden är nu komplett</span>
							</label>
						</div>
						<p className="foot-note" style={{marginTop: 6}}>
							&quot;Månaden är nu komplett&quot; låter månaden räknas som helt bokförd i burn och
							run-rate. Delvis bokförd status kommer annars från fakturaregistret, som inte
							vet något om det du just fyllt i för hand.
						</p>
						<label className="field" style={{marginTop: 4}}>
							<span>Anteckning</span>
							<input className="search" placeholder="Uppskattad utifrån juli. Ersätts när revisorn bokfört."
								value={form.note} onChange={e => setForm(f => ({...f, note: e.target.value}))} />
						</label>
						<button className="btn btn--primary" style={{marginTop: 12}} disabled={busy || delta !== 0}
							onClick={submit}>Spara skuggverifikat</button>
					</div>
				</div>

				<div className="table-wrap">
					<div className="chart__head" style={{padding: '14px 16px 0'}}>
						<span className="chart__title">Resultatpåverkan per månad</span>
					</div>
					<table style={{minWidth: 0}}>
						<thead><tr><th>Månad</th><th>Verifikat</th><th>Komplett</th><th>Resultatpåverkan</th></tr></thead>
						<tbody>
							{summary.months.length === 0 && (
								<tr><td colSpan={4}>Ingen skuggbokföring ännu.</td></tr>)}
							{summary.months.map(m => (
								<tr key={m.month}>
									<td>{m.month}</td><td>{m.count}</td>
									<td>{m.sealed ? 'ja' : '—'}</td>
									<td className="num--strong">{kr(m.result)}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>

			<div className="table-wrap">
				<table style={{minWidth: 720}}>
					<thead><tr>
						<th>Nr</th><th>Datum</th><th>Beskrivning</th><th>Konton</th>
						<th>Belopp</th><th>Komplett</th><th />
					</tr></thead>
					<tbody>
						{vouchers.length === 0 && (
							<tr><td colSpan={7}>Ingen skuggbokföring. Siffrorna visar enbart bokförda utfall.</td></tr>)}
						{vouchers.map(v => {
							const debit = v.rows.filter(r => r.amount > 0).reduce((a, b) => a + b.amount, 0) / 100;
							return (
								<tr key={v._id}>
									<td>{v._id}</td><td>{v.date}</td>
									<td>{v.description}{v.note ? ` — ${v.note}` : ''}</td>
									<td>{v.rows.map(r => r.account).join(', ')}</td>
									<td className="num--strong">{kr(debit)}</td>
									<td>{v.seals_month ? 'ja' : '—'}</td>
									<td><button className="btn btn--sm" onClick={() => remove(v._id)}>Ta bort</button></td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>

			<p className="foot-note">
				Skuggverifikat lagras i <code>shadow_vouchers</code> och blandas aldrig med
				Fortnox-spegeln (<code>vouchers</code>) eller Jabrs egna böcker
				(<code>jabr_vouchers</code>). De kan inte skickas till Fortnox — det finns ingen
				sådan väg i API:t. När revisorn bokfört på riktigt och synken hämtat hem det,
				jämförs varje skuggverifikat mot den baslinje som sparades när det skapades, och
				det som ser dubbelbokat ut flaggas här ovan.
			</p>
		</>
	);
}
